import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import DepartmentColumn from './DepartmentColumn'
import { dataService } from '../../services/dataService'
import useHorizontalWheel from '../../hooks/useHorizontalWheel'
import SearchInput from '../common/SearchInput'
import { filterDepartmentsWithFilteredPositions, getFilteredSearchResultsCount } from '../../utils/searchUtils'

const CareerMatrix = ({ selectedPosition, highlightedPositions, onPositionClick }) => {
  const [organizationData, setOrganizationData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  
  // Хук для горизонтальной прокрутки колёсиком мыши и программной прокрутки
  const { 
    containerRef: horizontalScrollRef, 
    canScrollLeft, 
    canScrollRight, 
    scrollLeft, 
    scrollRight,
    isMouseOver,
    hasFocus
  } = useHorizontalWheel(1.2, 0.9)
  
  // Загрузка данных из БД
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        console.log('🔄 Загружаю данные матрицы из БД...')
        
        // Инициализация происходит автоматически в dataService
        const departments = await dataService.getDepartmentsWithPositions()
        console.log(`📊 Загружено ${departments.length} отделов из API`)
        
        setOrganizationData(departments)
        setIsLoading(false)
      } catch (error) {
        console.error('Ошибка загрузки данных матрицы:', error)
        setIsLoading(false)
      }
    }
    
    loadData()
  }, [])
  
  // Анимация для контейнера
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  }
  
  // Фильтрация организационных данных по поисковому запросу (только должности)
  const filteredOrganizationData = filterDepartmentsWithFilteredPositions(organizationData, searchTerm)
  const searchResults = getFilteredSearchResultsCount(organizationData, searchTerm)

  // Сбрасываем горизонтальный скролл, когда набор отделов заметно меняется (например, очистка поиска)
  useEffect(() => {
    if (!horizontalScrollRef.current) return

    // Прокручиваем влево плавно
    horizontalScrollRef.current.scrollTo({ left: 0, behavior: 'smooth' })
  }, [searchTerm, filteredOrganizationData.length])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Загружаю данные матрицы...</div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Заголовок матрицы */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-6 text-center"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Организационная структура
        </h3>
        <div className="flex justify-center space-x-4 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-secondary-500 rounded"></div>
            <span>Карьерный рост</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary-500 rounded"></div>
            <span>Выбранная должность</span>
          </div>
        </div>
      </motion.div>

      {/* Поиск по должностям */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mb-6"
      >
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="flex items-center justify-between">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Поиск по названиям должностей..."
              className="flex-1 max-w-md"
            />
                         {(searchTerm || filteredOrganizationData.length !== organizationData.length) && (
               <div className="text-sm text-gray-500">
                 Найдено: {searchResults.departments} отдел{searchResults.departments === 1 ? '' : searchResults.departments < 5 ? 'а' : 'ов'}, {searchResults.positions} должност{searchResults.positions === 1 ? 'ь' : searchResults.positions < 5 ? 'и' : 'ей'}
               </div>
             )}
          </div>
        </div>
      </motion.div>

      {/* Контейнер с навигацией и матрицей */}
      <div className="relative">
        {/* Навигационные кнопки для десктопа и мобильных */}
        <div className="flex justify-between items-center mb-4">
          <motion.button
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            className={`
              flex items-center justify-center w-12 h-12 sm:w-10 sm:h-10 rounded-full border-2 transition-all duration-200
              ${canScrollLeft 
                ? 'border-blue-500 text-blue-500 hover:bg-blue-50 active:bg-blue-100' 
                : 'border-gray-300 text-gray-300 cursor-not-allowed'
              }
            `}
            whileHover={canScrollLeft ? { scale: 1.05 } : {}}
            whileTap={canScrollLeft ? { scale: 0.95 } : {}}
          >
            <ChevronLeft className="w-6 h-6 sm:w-5 sm:h-5" />
          </motion.button>

          <div className="text-xs sm:text-sm text-gray-500 hidden xs:block sm:block text-center px-2">
            <span className="hidden sm:inline">💡 Используйте колёсико мыши, стрелки ←→ или кнопки для прокрутки</span>
            <span className="sm:hidden">👆 Используйте кнопки навигации или свайп для прокрутки</span>
            {(isMouseOver || hasFocus) && <span className="text-blue-500 font-medium"> ⚡ Активно</span>}
          </div>

          <motion.button
            onClick={scrollRight}
            disabled={!canScrollRight}
            className={`
              flex items-center justify-center w-12 h-12 sm:w-10 sm:h-10 rounded-full border-2 transition-all duration-200
              ${canScrollRight 
                ? 'border-blue-500 text-blue-500 hover:bg-blue-50 active:bg-blue-100' 
                : 'border-gray-300 text-gray-300 cursor-not-allowed'
              }
            `}
            whileHover={canScrollRight ? { scale: 1.05 } : {}}
            whileTap={canScrollRight ? { scale: 0.95 } : {}}
          >
            <ChevronRight className="w-6 h-6 sm:w-5 sm:h-5" />
          </motion.button>
        </div>

        {/* Скрытые инструкции для screen readers */}
        <p id="scroll-instructions" className="sr-only">
          Используйте колесо мыши, свайп или стрелки для прокрутки
        </p>

        {/* Горизонтальная матрица отделов */}
        <div
          ref={horizontalScrollRef}
          role="region"
          aria-label="Горизонтальная матрица карьерных переходов"
          aria-describedby="scroll-instructions"
          aria-live="polite"
          tabIndex="0"
          className={`
            flex gap-6 overflow-x-auto overflow-y-hidden pb-4 scrollbar-hide 
            career-matrix-container horizontal-scroll-container transition-all duration-200
            ${isMouseOver || hasFocus ? 'ring-2 ring-blue-200 ring-opacity-50' : ''}
          `}
          style={{
            scrollBehavior: 'auto',
            WebkitOverflowScrolling: 'touch',
            position: 'relative',
            cursor: isMouseOver ? 'grab' : 'default'
          }}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex gap-6"
          >
            <AnimatePresence>
              {filteredOrganizationData.map((department, index) => (
                <motion.div
                  key={`${department.id ?? department.name}-${searchTerm ? 'filtered' : 'all'}`}
                  layout
                  className="flex-shrink-0 w-full sm:w-80 min-w-[280px] sm:min-w-[320px] max-w-[340px] horizontal-scroll-item"
                  variants={{
                    hidden: { x: 50, opacity: 0 },
                    visible: (i) => ({
                      x: 0,
                      opacity: 1,
                      transition: { duration: 0.3, delay: i * 0.05 }
                    })
                  }}
                  initial={false}
                  animate="visible"
                  custom={index}
                >
                <DepartmentColumn
                  department={department}
                  selectedPosition={selectedPosition}
                  highlightedPositions={highlightedPositions}
                  onPositionClick={onPositionClick}
                  index={index}
                  searchTerm={searchTerm}
                />
              </motion.div>
            ))}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Мобильная подсказка */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
          className="text-center mt-4 text-sm text-gray-400 sm:hidden"
        >
          👆 Используйте кнопки навигации или свайп для прокрутки
        </motion.div>
      </div>

      {/* Сообщение когда ничего не найдено */}
      {filteredOrganizationData.length === 0 && organizationData.length > 0 && searchTerm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12 bg-white rounded-xl shadow-sm border mb-8"
        >
          <div className="text-gray-400 mb-4">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🔍</span>
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ничего не найдено</h3>
          <p className="text-gray-500 mb-4">По запросу "{searchTerm}" ничего не найдено</p>
          <button
            onClick={() => setSearchTerm('')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Очистить поиск
          </button>
        </motion.div>
      )}

      {/* Легенда */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-8 p-4 bg-white rounded-xl shadow-sm border"
      >
        <h4 className="text-sm font-medium text-gray-700 mb-3">Условные обозначения:</h4>
        <div className="grid grid-cols-1 xs:grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-4 text-xs text-gray-600">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gradient-to-r from-green-400 to-green-500 rounded"></div>
            <span>Вертикальный</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gradient-to-r from-blue-400 to-blue-500 rounded"></div>
            <span>Горизонтальный</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gradient-to-r from-orange-400 to-orange-500 rounded"></div>
            <span>Межфункциональный</span>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default CareerMatrix 